// src/components/Seller/SellerOrders.js
import React, { useEffect, useState } from "react";
import config from "../../config";
import "../../styles/SellerOrders.css";

export default function SellerOrders({ sellerId }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusComment, setStatusComment] = useState("");
  const [trackingId, setTrackingId] = useState("");
  const [courierName, setCourierName] = useState("");

  // 🔹 Status history state
  const [statusHistory, setStatusHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");

  // 🔹 Invoice download state
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  // 🔐 Helper to attach auth header (update key name if needed)
  const getAuthHeaders = () => {
    const token =
      localStorage.getItem("authToken") ||
      localStorage.getItem("firebaseToken"); // fallback

    if (!token) return {};
    return {
      Authorization: `Bearer ${token}`,
    };
  };

  // Fetch orders for this seller
  useEffect(() => {
    if (!sellerId) return;
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId]);

  // Clear comment & history when switching selected order
  useEffect(() => {
    setStatusComment("");
    setTrackingId("");
    setCourierName("");
    if (selectedOrderId) {
      fetchStatusHistory(selectedOrderId);
    } else {
      setStatusHistory([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderId]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(
        `${config.baseUrl}/api/orders/seller/${sellerId}`,
        {
          headers: {
            ...getAuthHeaders(), // send token if present
          },
        }
      );

      if (!res.ok) throw new Error("Failed to load seller orders");
      const data = await res.json();
      const safeData = Array.isArray(data) ? data : [];

      // Sort orders by creation date (latest first)
      const sortedOrders = safeData.sort((a, b) => {
        const dateA = new Date(a.createdDate || 0);
        const dateB = new Date(b.createdDate || 0);
        return dateB - dateA; // Descending order (latest first)
      });

      setOrders(sortedOrders);

      if (safeData.length > 0) {
        setSelectedOrderId(safeData[0].orderId);
      } else {
        setSelectedOrderId(null);
      }
    } catch (e) {
      console.error(e);
      setError(e.message || "Error loading orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusHistory = async (orderId) => {
    try {
      setLoadingHistory(true);
      setHistoryError("");

      const res = await fetch(
        `${config.baseUrl}/api/orders/${orderId}/status-history`,
        {
          headers: {
            ...getAuthHeaders(),
          },
        }
      );

      if (!res.ok) {
        throw new Error("Failed to load status history");
      }

      const data = await res.json();
      setStatusHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setHistoryError(e.message || "Error loading status history");
      setStatusHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const formatDateTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatMoney = (val) =>
    typeof val === "number" ? `₹${val.toFixed(2)}` : "₹0.00";

  const getOrderItemCount = (order) =>
    (order.items || []).reduce((sum, item) => sum + (item.quantity || 0), 0);

  const getOrderSellerEarning = (order) =>
    (order.items || []).reduce(
      (sum, item) => sum + (item.sellerEarning || 0),
      0
    );

  // ------ FILTERED ORDERS (search + status) ------
  const filteredOrders = orders.filter((o) => {
    let ok = true;

    if (searchTerm.trim()) {
      const t = searchTerm.trim().toLowerCase();
      const customerName = (o.shippingAddress || "").split("\n")[0] || "";
      ok =
        o.orderId.toLowerCase().includes(t) ||
        String(o.customerId).includes(t) ||
        (o.phoneNumber && o.phoneNumber.includes(t)) ||
        customerName.toLowerCase().includes(t);
    }

    if (ok && statusFilter !== "ALL") {
      ok = o.status === statusFilter;
    }

    return ok;
  });

  const selectedOrder =
    filteredOrders.find((o) => o.orderId === selectedOrderId) ||
    filteredOrders[0] ||
    null;

  // ------ DASHBOARD STATS ------
  const totalOrders = orders.length;
  const totalItemsSold = orders.reduce(
    (sum, o) => sum + getOrderItemCount(o),
    0
  );
  const totalEarnings = orders.reduce(
    (sum, o) => sum + getOrderSellerEarning(o),
    0
  );
  const totalCommission = orders.reduce(
    (sum, o) =>
      sum +
      (o.items || []).reduce(
        (s, it) => s + (it.commissionAmount || 0),
        0
      ),
    0
  );

  // ------ STATUS UPDATE (ACTION) ------
  const updateOrderStatus = async (orderId, newStatus) => {
    if (!orderId || !newStatus) return;

    // For SHIPPED status, validate required fields
    if (newStatus === 'SHIPPED') {
      if (!trackingId.trim()) {
        alert('Please enter a tracking ID for shipped orders');
        return;
      }
      if (!courierName.trim()) {
        alert('Please enter a courier partner name for shipped orders');
        return;
      }
    }

    if (!window.confirm(`Change order ${orderId} status to ${newStatus}?`))
      return;

    try {
      setUpdatingStatus(true);
      setError("");

      const commentToSend =
        statusComment.trim() ||
        `Status updated to ${newStatus} by seller portal`;

      const requestBody = {
        status: newStatus,
        comment: commentToSend,
        sellerId: sellerId, // pass sellerId to backend (for future checks)
      };

      // Add tracking details for SHIPPED status
      if (newStatus === 'SHIPPED') {
        requestBody.trackingId = trackingId.trim();
        requestBody.courierName = courierName.trim();
      }

      const res = await fetch(
        `${config.baseUrl}/api/orders/${orderId}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(), // send token here as well
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.message || "Failed to update status");
      }

      // Clear comment and tracking fields after a successful update
      setStatusComment("");
      setTrackingId("");
      setCourierName("");

      // Refresh orders + history after update
      await fetchOrders();
      await fetchStatusHistory(orderId);
    } catch (e) {
      console.error(e);
      setError(e.message || "Error updating order status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // ------ DOWNLOAD INVOICE (ACTION) ------
  const downloadInvoice = async (orderId) => {
    if (!orderId) return;

    try {
      setDownloadingInvoice(true);
      setError("");

      const res = await fetch(
        `${config.baseUrl}/api/orders/${orderId}/invoice`,
        {
          headers: {
            Accept: "application/pdf", // 👈 ask explicitly for PDF
            ...getAuthHeaders(),
          },
        }
      );

      const contentType = res.headers.get("Content-Type") || "";

      if (!res.ok) {
        if (contentType.includes("application/json")) {
          const errJson = await res.json().catch(() => ({}));
          throw new Error(errJson.message || "Failed to download invoice");
        }
        throw new Error("Failed to download invoice");
      }

      // Safety: if backend accidentally returns JSON again, don't save fake PDF
      if (!contentType.startsWith("application/pdf")) {
        const text = await res.text();
        console.error("Unexpected invoice response:", text);
        throw new Error(
          "Server returned non-PDF data for invoice. Please check backend."
        );
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${orderId}-invoice.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setError(e.message || "Error downloading invoice");
    } finally {
      setDownloadingInvoice(false);
    }
  };

  // ------ RENDER STATES ------
  if (!sellerId) {
    return (
      <div className="seller-orders-section">
        <div className="seller-orders-empty">
          <h2>Your Orders</h2>
          <p>Seller ID is missing.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="seller-orders-section">
        <div className="seller-orders-loading">Loading your orders…</div>
      </div>
    );
  }

  if (error && orders.length === 0) {
    return (
      <div className="seller-orders-section">
        <div className="seller-orders-error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="seller-orders-section">
      {/* HEADER */}
      <div className="seller-orders-header">
        <div>
          <h2>🧾 Your Orders</h2>
          <p>View and manage all orders containing your products.</p>
        </div>
        <div className="seller-orders-header-right">
          <span className="seller-orders-seller-pill">
            Seller ID: <strong>{sellerId}</strong>
          </span>
          <button className="seller-orders-refresh" onClick={fetchOrders}>
            ⟳ Refresh
          </button>
        </div>
      </div>

      {/* STATS */}
      <div className="seller-orders-stats">
        <div className="seller-stat-card">
          <div className="seller-stat-label">Total Orders</div>
          <div className="seller-stat-value">{totalOrders}</div>
        </div>
        <div className="seller-stat-card">
          <div className="seller-stat-label">Items Sold</div>
          <div className="seller-stat-value">{totalItemsSold}</div>
        </div>
        <div className="seller-stat-card">
          <div className="seller-stat-label">Your Earnings</div>
          <div className="seller-stat-value">
            {formatMoney(totalEarnings)}
          </div>
        </div>
        <div className="seller-stat-card">
          <div className="seller-stat-label">Commission to AuraKart</div>
          <div className="seller-stat-value">
            {formatMoney(totalCommission)}
          </div>
        </div>
      </div>

      {/* FILTERS */}
      <div className="seller-orders-filters">
        <input
          type="text"
          placeholder="Search by Order ID, Customer, Phone…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="ALL">All Status</option>
          <option value="PAID">PAID</option>
          <option value="PROCESSING">PROCESSING</option>
          <option value="SHIPPED">SHIPPED</option>
          <option value="DELIVERED">DELIVERED</option>
          <option value="CANCELLED">CANCELLED</option>
        </select>
      </div>

      {error && (
        <div className="seller-orders-inline-error">⚠ {error}</div>
      )}

      {/* MAIN LAYOUT: TABLE + DETAILS */}
      <div className="seller-orders-layout">
        {/* LEFT: TABLE */}
        <div className="seller-orders-table-wrapper">
          {filteredOrders.length === 0 ? (
            <div className="seller-orders-empty">
              <div className="seller-orders-empty-icon">📭</div>
              <h3>No orders found</h3>
              <p>Try changing the filters or date range.</p>
            </div>
          ) : (
            <table className="seller-orders-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Type</th>
                  <th>Placed By</th>
                  <th>Placed On</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Items</th>
                  <th>Order Total</th>
                  <th>Your Earnings</th>
                  <th>Status</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => {
                  const itemCount = getOrderItemCount(order);
                  const sellerEarning = getOrderSellerEarning(order);
                  const shippingName = (order.shippingAddress || "")
                    .split("\n")[0]
                    .trim();

                  return (
                    <tr
                      key={order.orderId}
                      className={
                        selectedOrderId === order.orderId ? "row-selected" : ""
                      }
                      onClick={() => setSelectedOrderId(order.orderId)}
                    >
                      <td>#{order.orderId}</td>

                      <td>
                        <span
                          className={`order-type-badge ${(
                            order.orderSource || "ONLINE"
                          ).toLowerCase()}`}
                        >
                          {order.orderSource || "ONLINE"}
                        </span>
                      </td>

                      <td>
                        <div className="seller-order-placed-by">
                          {order.createdByName || "Customer"}
                        </div>

                        <div className="seller-order-channel">
                          {order.salesChannel || "Website"}
                        </div>
                      </td>

                      <td>{formatDateTime(order.createdDate)}</td>
                      <td>{shippingName || `ID ${order.customerId}`}</td>
                      <td>{order.phoneNumber || "-"}</td>
                      <td>{itemCount}</td>
                      <td>{formatMoney(order.totalAmount)}</td>
                      <td>{formatMoney(sellerEarning)}</td>
                      <td>
                        <span
                          className={`badge-status badge-${(
                            order.status || ""
                          ).toLowerCase()}`}
                        >
                          {order.status}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="seller-orders-view-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOrderId(order.orderId);
                          }}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          className="seller-orders-download-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadInvoice(order.orderId);
                          }}
                          disabled={downloadingInvoice}
                        >
                          {downloadingInvoice ? "Downloading…" : "Invoice"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* RIGHT: ORDER DETAILS PANEL */}
        <div className="seller-orders-details-panel">
          {!selectedOrder ? (
            <div className="seller-orders-details-empty">
              <h3>Select an order</h3>
              <p>Click on an order row on the left to view full details.</p>
            </div>
          ) : (
            <>
              <div className="seller-orders-details-header">
                <div>
                  <h3>Order #{selectedOrder.orderId}</h3>
                  <p>{formatDateTime(selectedOrder.createdDate)}</p>
                </div>
                <span
                  className={`badge-status badge-${(
                    selectedOrder.status || ""
                  ).toLowerCase()}`}
                >
                  {selectedOrder.status}
                </span>
              </div>

              <div className="seller-orders-details-section">
                <h4>Customer & Shipping</h4>
                <p className="shipping-address">
                  {(selectedOrder.shippingAddress || "")
                    .split("\n")
                    .map((line, idx) => (
                      <span key={idx}>
                        {line}
                        <br />
                      </span>
                    ))}
                </p>
                <p>
                  <strong>Phone:</strong> {selectedOrder.phoneNumber || "-"}
                </p>
              </div>

              <div className="seller-orders-details-section">
                <h4>Items</h4>
                <div className="seller-orders-items-list">
                  {(selectedOrder.items || []).map((item) => (
                    <div
                      key={item.id}
                      className="seller-orders-item-card"
                    >
                      <div className="seller-orders-item-left">
                        <div className="seller-orders-item-img">
                          <img
                            src={
                              item.primaryImageUrl ||
                              "https://via.placeholder.com/80x100?text=No+Image"
                            }
                            alt={item.productName}
                          />
                        </div>
                        <div className="seller-orders-item-info">
                          <div className="item-name">
                            {item.productName}
                          </div>
                          <div className="item-meta">
                            <span>SKU: {item.sku}</span>
                            <span>Color: {item.color}</span>
                            <span>Size: {item.sizeName}</span>
                          </div>
                          <div className="item-desc">
                            {item.productDescription}
                          </div>
                        </div>
                      </div>

                      <div className="seller-orders-item-right">
                        <div className="money-block">
                          <span className="label">Qty</span>
                          <span className="value">{item.quantity}</span>
                        </div>
                        <div className="money-block">
                          <span className="label">Offer Price</span>
                          <span className="value">
                            {formatMoney(item.offerPrice)}
                          </span>
                        </div>
                        <div className="money-block">
                          <span className="label">
                            Commission ({item.commissionPercent}%)
                          </span>
                          <span className="value">
                            {formatMoney(item.commissionAmount)}
                          </span>
                        </div>
                        <div className="money-block highlight">
                          <span className="label">Your Earning</span>
                          <span className="value">
                            {formatMoney(item.sellerEarning)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="seller-orders-details-section totals-section">
                <div>
                  <p>
                    <strong>Order Total:</strong>{" "}
                    {formatMoney(selectedOrder.totalAmount)}
                  </p>
                  <p>
                    <strong>Your Total Earning:</strong>{" "}
                    {formatMoney(getOrderSellerEarning(selectedOrder))}
                  </p>
                </div>
              </div>

              {/* STATUS HISTORY */}
              <div className="seller-orders-details-section status-history-section">
                <h4>Status History</h4>
                {loadingHistory ? (
                  <p className="status-history-loading">Loading history…</p>
                ) : statusHistory.length === 0 ? (
                  <p className="status-history-empty">
                    No status updates recorded yet.
                  </p>
                ) : (
                  <ul className="status-history-list">
                    {statusHistory.map((h, idx) => (
                      <li key={idx} className="status-history-row">
                        <div className="status-history-main">
                          <span
                            className={`status-pill badge-status badge-${(
                              h.status || ""
                            ).toLowerCase()}`}
                          >
                            {h.status}
                          </span>
                          <span className="status-history-meta">
                            {h.changedByName || "System"} •{" "}
                            {formatDateTime(h.createdAt)}
                          </span>
                        </div>
                        {h.comment && (
                          <div className="status-history-comment">
                            {h.comment}
                          </div>
                        )}
                        {h.trackingId && (
                          <div className="status-history-tracking">
                            Tracking ID: {h.trackingId}
                            {h.courierName && ` (${h.courierName})`}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {historyError && (
                  <p className="status-history-error">⚠ {historyError}</p>
                )}
              </div>

              {/* ACTIONS */}
              <div className="seller-orders-details-section actions-section">
                <h4>Actions</h4>
                <p className="actions-help">
                  Update the order status as you process the order or download
                  the invoice.
                </p>

                <div className="actions-buttons actions-buttons-top">
                  <button
                    className="seller-orders-download-btn"
                    onClick={() => downloadInvoice(selectedOrder.orderId)}
                    disabled={downloadingInvoice}
                  >
                    {downloadingInvoice
                      ? "Downloading…"
                      : "Download Invoice"}
                  </button>
                </div>

                <div className="status-comment-block">
                  <label htmlFor="statusComment">
                    Status Comment (optional)
                  </label>
                  <textarea
                    id="statusComment"
                    placeholder="Eg. Order packed and ready for pickup..."
                    value={statusComment}
                    onChange={(e) => setStatusComment(e.target.value)}
                  />
                </div>

                <div className="tracking-fields-block">
                  <div className="tracking-field">
                    <label htmlFor="trackingId">
                      Tracking ID <span style={{ color: '#ff6b6b', fontSize: '12px' }}>(Required for Shipped status)</span>
                    </label>
                    <input
                      id="trackingId"
                      type="text"
                      placeholder="Enter tracking/AWB number"
                      value={trackingId}
                      onChange={(e) => setTrackingId(e.target.value)}
                    />
                  </div>
                  <div className="tracking-field">
                    <label htmlFor="courierName">
                      Courier Partner <span style={{ color: '#ff6b6b', fontSize: '12px' }}>(Required for Shipped status)</span>
                    </label>
                    <select
                      id="courierName"
                      value={courierName}
                      onChange={(e) => setCourierName(e.target.value)}
                    >
                      <option value="">Select courier partner</option>
                      <option value="DTDC">DTDC</option>
                      <option value="Delhivery">Delhivery</option>
                      <option value="Blue Dart">Blue Dart</option>
                      <option value="FedEx">FedEx</option>
                      <option value="Aramex">Aramex</option>
                      <option value="Ecom Express">Ecom Express</option>
                      <option value="XpressBees">XpressBees</option>
                      <option value="Shadowfax">Shadowfax</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="actions-buttons">
                  <button
                    disabled={updatingStatus}
                    onClick={() =>
                      updateOrderStatus(
                        selectedOrder.orderId,
                        "PROCESSING"
                      )
                    }
                  >
                    Mark as Processing
                  </button>
                  <button
                    disabled={updatingStatus}
                    onClick={() =>
                      updateOrderStatus(
                        selectedOrder.orderId,
                        "SHIPPED"
                      )
                    }
                  >
                    Mark as Shipped
                  </button>
                  <button
                    disabled={updatingStatus}
                    onClick={() =>
                      updateOrderStatus(
                        selectedOrder.orderId,
                        "DELIVERED"
                      )
                    }
                  >
                    Mark as Delivered
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
